import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useConditionalSubscription } from 'features/common/security/hooks';
import { useAuthUserData, useToast, useParams, useSimpleError, useQueryWithSimpleErrorHandling } from 'hooks';
import { OFFER_TYPES_QUERY } from '../queries';
import { INSURANCE_TYPES_QUERY } from 'features/insuranceDocument/queries/insuranceDocumentQueries';
import { CREATE_INSURANCE_DOCUMENT, UPDATE_INSURANCE_DOCUMENT } from '../mutations';
import { INSURANCE_DOCUMENT_CREATED } from '../subscriptions';
import { INSURANCE_DOCUMENT_UPDATED } from 'features/insuranceDocument/subscriptions/insuranceDocumentSubscriptions';
import { useSubscription } from '@apollo/client';
import { useChangeTrackingLens, get } from '@totalsoft/change-tracking-react';
import { shape, required } from '@totalsoft/pure-validations';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Prompt } from 'react-router-dom';
import { isDirty } from '@totalsoft/change-tracking';
import { env } from 'utils/env';
import GeneralDataComponent from './GeneralDataComponent';

const GeneralDataContainer = ({ insuranceData, insuranceTypeCode, cascoInsuranceTypeCode, isNew, dirtyInfoRef, setDirtyInfoRef }) => {
  const { authUser } = useAuthUserData();
  const { t } = useTranslation();
  const addToast = useToast();
  const showError = useSimpleError();
  const history = useHistory();
  const [showSearchModal, setShowSearchModal] = useState(false);

  const validator = useMemo(() => {
    return shape({
      offerTypeId: required,
      insuranceTypeId: required
    });
  }, []);

  const [validation, validate] = useDirtyFieldValidation(validator);

  const [generalDataLens, generalDataDirtyInfo, resetGeneralDataLens] = useChangeTrackingLens({});
  const [saving, setSaving] = useState(false);
  const [saveDisabled, setSaveDisabled] = useState(!isNew);

  const { data: offerTypesData } = useQueryWithSimpleErrorHandling(OFFER_TYPES_QUERY);
  const { data: insuranceTypesData } = useQueryWithSimpleErrorHandling(INSURANCE_TYPES_QUERY, {
    variables: { onlyActive: isNew }
  });

  const offerTypeConfigurationJSON = useParams('OfferTypeCodesConfiguration');

  const offerTypeCodes = useMemo(() => {
    if (offerTypeConfigurationJSON) return JSON.parse(offerTypeConfigurationJSON);
  }, [offerTypeConfigurationJSON]);

  useEffect(() => {
    if (insuranceData) {
      resetGeneralDataLens(insuranceData);
    }
  }, [insuranceData, resetGeneralDataLens]);

  const offerTypes = offerTypesData && offerTypesData.offerTypes;
  const insuranceTypes = insuranceTypesData && insuranceTypesData.insuranceTypes;

  const defaultOfferType = offerTypes && offerTypes.find(x => x.implicit)?.id;
  const defaultInsuranceType = insuranceTypes && insuranceTypes.find(x => x.implicit)?.id;

  const maintainPolicyOption = useMemo(() => {
    return (
      insuranceTypeCode === cascoInsuranceTypeCode &&
      offerTypeCodes &&
      offerTypes &&
      offerTypes.length > 0 &&
      offerTypes.some(o => o.code === offerTypeCodes?.Cession || o.code === offerTypeCodes.Restructuring) &&
      ((generalDataLens.offerTypeId |> get) === offerTypes.find(o => o.code === offerTypeCodes.Cession).id ||
        (generalDataLens.offerTypeId |> get) === offerTypes.find(o => o.code === offerTypeCodes.Restructuring).id)
    );
  }, [insuranceTypeCode, offerTypeCodes, offerTypes, generalDataLens.offerTypeId, cascoInsuranceTypeCode]);

  useEffect(() => {
    if (isNew && !(generalDataLens.insuranceTypeId |> get)) {
      resetGeneralDataLens({
        ...(generalDataLens |> get),
        insuranceTypeId: defaultInsuranceType
      });
    }
  }, [isNew, defaultInsuranceType, generalDataLens, generalDataLens.insuranceTypeId, resetGeneralDataLens]);

  useEffect(() => {
    if (isNew && !(generalDataLens.userName |> get)) {
      resetGeneralDataLens({
        ...(generalDataLens |> get),
        userName: authUser?.userName
      });
    }
  }, [isNew, authUser, generalDataLens, generalDataLens.userName, resetGeneralDataLens]);

  useEffect(() => {
    if (isNew && !(generalDataLens.offerTypeId |> get)) {
      resetGeneralDataLens({
        ...(generalDataLens |> get),
        offerTypeId: defaultOfferType
      });
    }
  }, [isNew, defaultOfferType, generalDataLens, generalDataLens.offerTypeId, resetGeneralDataLens]);

  const [createInsuranceDocument] = useMutation(CREATE_INSURANCE_DOCUMENT, {
    onError: error => {
      setSaving(false);
      showError(error);
    },
    onCompleted: _ => {
      resetGeneralDataLens({ ...(generalDataLens |> get) });
    }
  });

  const [updateInsuranceDocument] = useMutation(UPDATE_INSURANCE_DOCUMENT, {
    onError: error => {
      setSaving(false);
      showError(error);
    },
    onCompleted: _ => {
      resetGeneralDataLens({ ...(generalDataLens |> get) });
    }
  });

  useEffect(() => {
    validate(generalDataLens |> get, generalDataDirtyInfo);
  }, [validate, generalDataLens, generalDataDirtyInfo]);

  const handleSave = useCallback(() => {
    if (!validate(generalDataLens |> get, generalDataDirtyInfo)) {
      return;
    }

    setSaving(true);
    isNew
      ? createInsuranceDocument({
          variables: {
            input: {
              insuranceTypeId: generalDataLens.insuranceTypeId |> get,
              offerTypeId: generalDataLens.offerTypeId |> get
            }
          }
        })
      : updateInsuranceDocument({
          variables: {
            input: {
              documentId: generalDataLens.documentId |> get,
              insuranceTypeId: generalDataLens.insuranceTypeId |> get,
              currentPolicyMaintained: generalDataLens.currentPolicyMaintained |> get
            }
          }
        });
  }, [createInsuranceDocument, generalDataDirtyInfo, generalDataLens, isNew, updateInsuranceDocument, validate]);

  useConditionalSubscription(INSURANCE_DOCUMENT_CREATED, {
    onData: ({ data: { data } }) => {
      addToast(t('General.Saved'), 'success', { toastId: 'InsuranceDocumentCreatedToast' });
      setSaving(false);
      if (isNew && data?.insuranceDocumentCreated?.documentId) {
        history.push(`/insurance-documents/${data.insuranceDocumentCreated.documentId}/generalData`);
      }
    }
  });

  useSubscription(INSURANCE_DOCUMENT_UPDATED, {
    variables: { documentId: generalDataLens.documentId |> get },
    skip: !(generalDataLens.documentId |> get),
    onData: () => {
      setSaving(false);
    }
  });

  useEffect(() => {
    const unsavedChanges = isDirty(generalDataDirtyInfo);
    !isNew && setSaveDisabled(!unsavedChanges);

    setDirtyInfoRef('generalData', unsavedChanges);
    const { onDirtyInfoChange } = dirtyInfoRef.current;
    onDirtyInfoChange && onDirtyInfoChange();
  }, [isNew, generalDataDirtyInfo, dirtyInfoRef, setDirtyInfoRef]);

  const financingOfferURL = useMemo(() => {
    if (insuranceData.financingDocumentNumber !== null)
      return `${env.REACT_APP_LSNG_ORIGINATION_URL}/offers/${insuranceData.financingDocumentId}/${Number(
        insuranceData.financingDocumentSiteId
      )}`;
  }, [insuranceData.financingDocumentId, insuranceData.financingDocumentSiteId, insuranceData.financingDocumentNumber]);

  const sourceDocumentURL = useMemo(() => {
    if (insuranceData.sourceDocumentNumber !== null && insuranceData.sourceDocumentId != null)
      return `/insurance-documents/${insuranceData.sourceDocumentId}`;
  }, [insuranceData.sourceDocumentId, insuranceData.sourceDocumentNumber]);

  return (
    <>
      <Prompt when={isDirty(generalDataDirtyInfo)} message={t('LeavingWithoutSaving')} />
      <GeneralDataComponent
        generalDataLens={generalDataLens}
        offerTypes={offerTypes}
        offerTypeCodes={offerTypeCodes}
        insuranceTypes={insuranceTypes}
        isNew={isNew}
        onSave={handleSave}
        saving={saving}
        saveDisabled={saveDisabled || saving}
        validation={validation}
        showSearchModal={showSearchModal}
        setShowSearchModal={setShowSearchModal}
        financingOfferURL={financingOfferURL}
        sourceDocumentURL={sourceDocumentURL}
        maintainPolicyOption={maintainPolicyOption}
      />
    </>
  );
};

GeneralDataContainer.propTypes = {
  insuranceData: PropTypes.object,
  insuranceTypeCode: PropTypes.string,
  cascoInsuranceTypeCode: PropTypes.string,
  isNew: PropTypes.bool.isRequired,
  dirtyInfoRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]).isRequired,
  setDirtyInfoRef: PropTypes.func.isRequired
};

export default GeneralDataContainer;
